import { useCart } from "../../../context/CartContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../../../components/ui/sheet";
import { X, Plus, Minus, ShoppingBag, Truck } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * SRC-styled slide-in cart drawer. Includes the "Add $X for FREE shipping"
 * progress bar. Free-shipping threshold comes from `config.pricing.freeShippingAt`
 * so different store types can adjust the anchor (e.g. $100 for clothing,
 * $50 for skincare).
 */
export default function CartDrawer({ config, basePath = "" }) {
  const cart = useCart();
  const { drawerOpen, setDrawerOpen, items, updateQty, removeItem, subtotal, count, lineKey } = cart;
  const threshold = config?.pricing?.freeShippingThreshold ?? 100;
  const remainingForFree = Math.max(0, threshold - subtotal);
  const freeShipPct = threshold > 0 ? Math.min(100, (subtotal / threshold) * 100) : 100;
  const accent = config?.brand?.accentColor || "#E60000";

  return (
    <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
      <SheetContent
        data-testid="cart-drawer"
        side="right"
        className="w-full sm:max-w-md p-0 flex flex-col bg-white"
      >
        <SheetHeader className="px-6 py-5 border-b border-neutral-200 flex flex-row items-center justify-between space-y-0">
          <SheetTitle className="font-heading text-base tracking-wide uppercase flex items-center gap-2">
            <ShoppingBag className="w-4 h-4" strokeWidth={1.5} /> Your Cart ({count})
          </SheetTitle>
          <SheetDescription className="sr-only">Review the items in your cart and proceed to checkout.</SheetDescription>
          <button data-testid="cart-close" onClick={() => setDrawerOpen(false)} className="p-1">
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </SheetHeader>

        <div className="px-6 py-3 bg-neutral-50 border-b border-neutral-200">
          {remainingForFree > 0 ? (
            <p className="text-xs text-neutral-700">
              Add <span className="font-semibold">${remainingForFree.toFixed(2)}</span> more for <span className="font-semibold">FREE shipping</span>
            </p>
          ) : (
            <p className="text-xs text-emerald-700 font-medium flex items-center gap-1">
              <Truck className="w-3.5 h-3.5" strokeWidth={1.5} /> You unlocked FREE shipping
            </p>
          )}
          <div className="mt-2 h-1 bg-neutral-200 overflow-hidden">
            <div className="h-full bg-black transition-all" style={{ width: `${freeShipPct}%` }} />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <div data-testid="cart-empty" className="h-full flex flex-col items-center justify-center p-10 text-center">
              <ShoppingBag className="w-10 h-10 text-neutral-300" strokeWidth={1} />
              <p className="mt-4 font-heading text-lg">Your cart is empty</p>
              <p className="mt-1 text-sm text-neutral-500">Find your next favourite piece.</p>
              <Link
                to={`${basePath}/shop`}
                onClick={() => setDrawerOpen(false)}
                className="mt-6 bg-black hover:bg-neutral-800 text-white px-8 py-4 text-xs uppercase tracking-widest transition-colors"
              >
                Shop Now
              </Link>
            </div>
          ) : (
            <ul className="divide-y divide-neutral-200">
              {items.map((it) => (
                <li key={lineKey(it)} data-testid={`cart-line-${it.slug}`} className="p-4 flex gap-4">
                  {it.image && (
                    <img src={it.image} alt={it.name} className="w-20 h-24 object-cover flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-heading text-sm truncate">{it.name}</p>
                      <button onClick={() => removeItem(lineKey(it))} data-testid={`cart-remove-${it.slug}`} className="p-1 text-neutral-400 hover:text-black">
                        <X className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </div>
                    <p className="text-xs text-neutral-500 mt-0.5">
                      {[it.size, it.color].filter(Boolean).join(" · ")}
                    </p>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="inline-flex items-center border border-neutral-300">
                        <button onClick={() => updateQty(lineKey(it), Math.max(1, it.quantity - 1))} className="w-7 h-7 grid place-items-center">
                          <Minus className="w-3 h-3" strokeWidth={1.5} />
                        </button>
                        <span data-testid={`cart-qty-${it.slug}`} className="w-8 text-center text-xs">{it.quantity}</span>
                        <button onClick={() => updateQty(lineKey(it), it.quantity + 1)} className="w-7 h-7 grid place-items-center">
                          <Plus className="w-3 h-3" strokeWidth={1.5} />
                        </button>
                      </div>
                      <p className="font-heading text-sm">${(it.price * it.quantity).toFixed(2)}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-neutral-200 p-6 space-y-4">
            <div className="flex items-center justify-between text-sm">
              <span>Subtotal</span>
              <span className="font-heading" data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
            </div>
            <Link
              to={`${basePath}/checkout`}
              onClick={() => setDrawerOpen(false)}
              data-testid="cart-checkout-btn"
              className="block w-full text-center bg-black hover:bg-neutral-800 text-white py-4 text-xs uppercase tracking-[0.2em] transition-colors"
              style={{ ["--accent"]: accent }}
            >
              Checkout →
            </Link>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
